/**
 * v0.4 追加要件⑪ 章③ (D-355/D-370/D-374): 日別サマリ (`/shift/report/daily`)。
 * 列=日付 / 行=メトリクス の転置マトリクスをノードごとにセクションで積む (§4-6)。
 * <768px は日付ドリル + メトリクス縦カード (U-10)。読み取り専用 (R-1、編集は編成ボードへの深いリンク)。
 *
 * 設計: docs/design/v04-cross-employee-shift-report-detail.md §4-6 / §11
 */
import { useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { useLabel } from '@/lib/labels';
import { formatCurrency, formatCount } from '@/lib/format';
import { useHolidays } from '@/hooks/useHolidays';
import { DateLabel } from '@/components/DateLabel';
import { EmptyState } from '@/components/EmptyState';
import { StoreMultiSelect, collectStoreIds } from '@/components/StoreMultiSelect';
import { LaborCostConfidenceToggle } from '@/components/LaborCostConfidenceToggle';
import { useScopeStore, findNodeInTree } from '@/stores/scope';
import { type NullReason, NULL_REASON_LABELS, OMITTED_REASON_LABELS, type LaborCostConfidence } from '@/lib/shift-report-labels';
import { thisMonthStart, nextMonthStart, enumerateDates } from '@/lib/shift-report-dates';
import { type ViewColumn, formatViewColumnValue, resolveViewColumnLabel } from '@/lib/shift-report-view-columns';

type DailyCell = {
  node_id: string; work_date: string;
  required_headcount: number | null; max_headcount: number | null;
  actual_headcount: number; headcount_diff: number | null;
  required_labor_minutes: number | null; staffing_satisfied: boolean | null;
  labor_minutes: number; labor_cost_amount: number | null;
  visitor_count: number | null; assignment_count: number;
  violation_counts: { hard: number; soft: number };
  null_reasons: Partial<Record<string, NullReason>>;
};

type DailyResponse = {
  range: { from: string; to: string };
  confidence: LaborCostConfidence;
  scope_summary: { node_count: number; person_count: number; cell_count: number };
  labor_cost_granularity: Record<string, 'daily' | 'monthly'>;
  omitted_metrics: Array<{ metric_key: string; reason: string }>;
  nodes: Array<{ node_id: string; node_name: string }>;
  cells: DailyCell[];
  // v0.4 追加要件⑪ 章⑦フォローアップ (D-348): /settings/report-views の既定ビューに列が
  // 1つでも設定されていれば、その列を使って表示する (null = 既存の固定表示のまま)。
  view_columns: ViewColumn[] | null;
};

type PeriodListItem = { period_id: string; scope_node_id: string; start_date: string; end_date: string };

const STORE_COLORS = ['#C91F28', '#F05A28', '#F2B705', '#6E80A8', '#1A1A1A', '#8D8D8D'];

function NullBadge({ reason }: { reason?: NullReason }) {
  if (!reason) return <span className="text-text-sub">—</span>;
  return <span className="text-text-sub" title={NULL_REASON_LABELS[reason]}>{NULL_REASON_LABELS[reason]}</span>;
}

type MetricRow = {
  key: string;
  labelKey: Parameters<ReturnType<typeof useLabel>>[0];
  fallback: string;
  render: (cell: DailyCell) => React.ReactNode;
};

export function ShiftReportDailyPage() {
  const t = useLabel();
  const scopeRoots = useScopeStore((s) => s.roots);
  const scopeSelectedNodeId = useScopeStore((s) => s.selectedNodeId);
  const scopedNodes = useMemo(() => {
    if (!scopeSelectedNodeId) return scopeRoots;
    const subtree = findNodeInTree(scopeRoots, scopeSelectedNodeId);
    return subtree ? [subtree] : scopeRoots;
  }, [scopeRoots, scopeSelectedNodeId]);

  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(new Set());
  const [from, setFrom] = useState(thisMonthStart());
  const [to, setTo] = useState(nextMonthStart(thisMonthStart()));
  const [confidence, setConfidence] = useState<LaborCostConfidence>('estimate');
  const [mobileDate, setMobileDate] = useState<string | null>(null);

  const nodeColorIndex = useMemo(() => {
    const ids = scopedNodes.flatMap(collectStoreIds);
    const map = new Map<string, number>();
    ids.forEach((id, idx) => map.set(id, idx));
    return map;
  }, [scopedNodes]);
  const colorOf = useCallback((storeId: string) => STORE_COLORS[(nodeColorIndex.get(storeId) ?? 0) % STORE_COLORS.length]!, [nodeColorIndex]);

  const nodeIdsParam = selectedNodeIds.size > 0 ? [...selectedNodeIds].join(',') : null;
  const { data, isLoading, isError, error, refetch } = useQuery<DailyResponse>({
    queryKey: ['shift-report-daily', nodeIdsParam, from, to, confidence],
    queryFn: () => apiFetch<DailyResponse>(
      `/shift-report/daily?from=${from}&to=${to}&confidence=${confidence}${nodeIdsParam ? `&node_ids=${encodeURIComponent(nodeIdsParam)}` : ''}`,
    ),
  });

  // R-1: 編成ボードへの深いリンク用に期間一覧を取得 (対象日を含む期間を探す)
  const { data: periodsData } = useQuery<{ periods: PeriodListItem[] }>({
    queryKey: ['shift-periods'],
    queryFn: () => apiFetch('/shift/periods'),
  });
  const findPeriodFor = useCallback((nodeId: string, date: string): PeriodListItem | undefined => {
    // scope_node_id は期間作成時のノードのため対象日の店舗そのものと一致しない場合がある。
    // 日付範囲の一致のみで判定する (複数該当時は最初のものを使う、稀なケースのため簡易実装)。
    void nodeId;
    return periodsData?.periods.find((p) => p.start_date <= date && date <= p.end_date);
  }, [periodsData]);

  const holidays = useHolidays(from, to, data?.nodes[0]?.node_id ?? scopeSelectedNodeId ?? '');

  const cellsByNodeDate = useMemo(() => {
    const map = new Map<string, DailyCell>();
    for (const c of data?.cells ?? []) map.set(`${c.node_id}|${c.work_date}`, c);
    return map;
  }, [data]);

  const dates = useMemo(() => (data ? enumerateDates(data.range.from, data.range.to) : []), [data]);

  const metricRows: MetricRow[] = [
    {
      key: 'required_headcount', labelKey: 'field.report_metric_catalog.node_required_headcount', fallback: '必要人数',
      render: (cell) => (cell.required_headcount !== null ? formatCount(cell.required_headcount, '名') : <NullBadge reason={cell.null_reasons.required_headcount} />),
    },
    {
      key: 'actual_headcount', labelKey: 'field.report_metric_catalog.node_actual_headcount', fallback: '実績人数',
      render: (cell) => formatCount(cell.actual_headcount, '名'),
    },
    {
      key: 'headcount_diff', labelKey: 'field.report_metric_catalog.node_headcount_diff', fallback: '過不足',
      render: (cell) => {
        if (cell.headcount_diff === null) return <NullBadge reason={cell.null_reasons.headcount_diff} />;
        const cls = cell.headcount_diff < 0 ? 'text-brand-red font-medium' : 'text-text-body';
        return <span className={cls}>{cell.headcount_diff > 0 ? `+${cell.headcount_diff}` : cell.headcount_diff}</span>;
      },
    },
    {
      key: 'required_labor_minutes', labelKey: 'field.report_metric_catalog.node_required_labor_minutes', fallback: '必要人時',
      render: (cell) => (cell.required_labor_minutes !== null ? `${(cell.required_labor_minutes / 60).toFixed(1)}h` : <NullBadge reason={cell.null_reasons.required_labor_minutes} />),
    },
    {
      key: 'labor_minutes', labelKey: 'field.report_metric_catalog.node_labor_minutes', fallback: '実績人時',
      render: (cell) => `${(cell.labor_minutes / 60).toFixed(1)}h`,
    },
    {
      key: 'labor_cost_amount', labelKey: 'field.report_metric_catalog.node_labor_cost_amount', fallback: '人件費見込み',
      render: (cell) => (cell.labor_cost_amount !== null ? formatCurrency(cell.labor_cost_amount) : <NullBadge reason={cell.null_reasons.labor_cost_amount} />),
    },
    {
      key: 'visitor_count', labelKey: 'field.report_metric_catalog.node_visitor_count', fallback: '来客数',
      render: (cell) => (cell.visitor_count !== null ? formatCount(cell.visitor_count, '名') : <NullBadge reason={cell.null_reasons.visitor_count} />),
    },
    {
      key: 'assignment_count', labelKey: 'field.report_metric_catalog.node_assignment_count', fallback: '出勤人数',
      render: (cell) => formatCount(cell.assignment_count, '名'),
    },
    {
      key: 'violation_count', labelKey: 'field.report_metric_catalog.node_violation_count', fallback: '違反件数',
      render: (cell) => {
        const { hard, soft } = cell.violation_counts;
        if (hard === 0 && soft === 0) return <span className="text-text-sub">0</span>;
        return <span className={hard > 0 ? 'text-brand-red font-medium' : 'text-text-body'}>{hard > 0 ? `重大${hard}・` : ''}軽微{soft}</span>;
      },
    },
  ];

  // v0.4 追加要件⑪ 章⑦フォローアップ (D-348、6並列レビューCRITICAL対応): /settings/report-views の
  // 既定ビューに列が設定されていれば、固定の9項目 (metricRows) の代わりにその列を描画する
  // (加算方式ではなく置換方式 — §4-6 の転置マトリクスは行=項目のため、項目自体を差し替える形が自然)。
  // 列が1つも設定されていなければ view_columns は null で、従来どおり固定表示のまま。
  // design-expert Major指摘: view_columns の label は管理者入力の label_override (最大100文字) または
  // metric_key 生値になりうり、sticky列 (常時ピン留め) が肥大化して日付列を圧迫し続ける。
  // labelText (title tooltip 用のプレーン文字列) を分離して truncate + title で吸収する。
  type DisplayRow = { key: string; label: React.ReactNode; labelText: string; render: (compositeKey: string, cell: DailyCell | undefined) => React.ReactNode };
  const fixedRows: DisplayRow[] = metricRows.map((row) => {
    const labelText = t(row.labelKey, row.fallback);
    return {
      key: row.key, label: labelText, labelText,
      render: (_compositeKey, cell) => (cell ? row.render(cell) : <span className="text-text-sub">—</span>),
    };
  });
  const viewColumnRows: DisplayRow[] | null = data?.view_columns && data.view_columns.length > 0
    ? data.view_columns.map((col) => ({
      key: col.column_id, label: resolveViewColumnLabel(t, col), labelText: resolveViewColumnLabel(t, col),
      render: (compositeKey) => {
        if (col.omitted) {
          return <span className="text-text-sub" title={col.omitted_reason ? (OMITTED_REASON_LABELS[col.omitted_reason] ?? col.omitted_reason) : undefined}>非表示</span>;
        }
        return <span>{formatViewColumnValue(col.values[compositeKey] ?? null, col.format_hint)}</span>;
      },
    }))
    : null;
  const displayRows = viewColumnRows ?? fixedRows;

  const rangeTooLarge = isError && error instanceof ApiClientError && error.code === 'REPORT_RANGE_TOO_LARGE';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-h1">{t('entity.shift_report_daily', '日別サマリ')}</h1>
      </div>

      <div className="border border-surface-border rounded-lg bg-white p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-text-sub mb-1" htmlFor="srd-from">開始日</label>
            <input
              id="srd-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              className="min-h-[44px] px-3 py-2 rounded border border-surface-border text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-text-sub mb-1" htmlFor="srd-to">終了日 (含まない)</label>
            <input
              id="srd-to" type="date" value={to} onChange={(e) => setTo(e.target.value)}
              className="min-h-[44px] px-3 py-2 rounded border border-surface-border text-sm"
            />
          </div>
          <LaborCostConfidenceToggle value={confidence} onChange={setConfidence} />
          <div>
            <span className="block text-xs text-text-sub mb-1">対象店舗</span>
            <StoreMultiSelect nodes={scopedNodes} selectedIds={selectedNodeIds} onChange={setSelectedNodeIds} colorOf={colorOf} savedViewNamespace="shift-report-daily" />
          </div>
        </div>
        {data && (
          <div className="text-xs text-text-sub" aria-live="polite">
            対象店舗 {data.scope_summary.node_count} / 対象者 {data.scope_summary.person_count}名
            {data.omitted_metrics.length > 0 && (
              <span className="ml-2 text-misespark-navy">
                (非表示の項目: {data.omitted_metrics.map((m) => OMITTED_REASON_LABELS[m.reason] ?? m.reason).join('・')})
              </span>
            )}
          </div>
        )}
      </div>

      {isLoading && <div className="py-10 text-center text-sm text-text-sub" aria-live="polite">読み込み中…</div>}

      {isError && !rangeTooLarge && (
        <EmptyState message="日別サマリの取得に失敗しました" description={error instanceof ApiClientError ? error.message : undefined} actionLabel="再読み込み" onAction={() => refetch()} />
      )}

      {isError && rangeTooLarge && (
        <EmptyState
          message="指定範囲が大きすぎます"
          description={error instanceof ApiClientError ? error.message : undefined}
          actionLabel="期間を1ヶ月に縮める"
          onAction={() => { setFrom(thisMonthStart()); setTo(nextMonthStart(thisMonthStart())); }}
        />
      )}

      {data && data.nodes.length === 0 && <EmptyState message="対象店舗がありません" description="対象店舗を選択してください" />}

      {data && data.nodes.length > 0 && (
        <>
          {/* ≥768px: 転置マトリクス (行=メトリクス/列=日付)、ノードごとにセクション */}
          <div className="hidden md:block space-y-6">
            {data.nodes.map((node) => (
              <div key={node.node_id} className="border border-surface-border rounded-lg bg-white overflow-hidden">
                <div className="px-4 py-2 border-b border-surface-border bg-surface-sunken flex items-center justify-between">
                  <span className="font-medium text-sm">{node.node_name}</span>
                  <span className="text-xs text-text-sub">
                    {data.labor_cost_granularity[node.node_id] === 'monthly' ? 'この店舗では人件費を月次でのみ開示する設定です' : ''}
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="border-collapse w-full text-sm">
                    <thead>
                      <tr>
                        <th scope="col" className="sticky left-0 z-10 bg-surface-sunken border-b border-r border-surface-border px-3 py-2 text-left text-xs text-text-sub" style={{ minWidth: 140 }}>
                          項目
                        </th>
                        {dates.map((date) => {
                          const cell = cellsByNodeDate.get(`${node.node_id}|${date}`);
                          const period = findPeriodFor(node.node_id, date);
                          return (
                            <th key={date} scope="col" className="relative border-b border-surface-border px-2 py-2 text-center text-xs whitespace-nowrap" style={{ minWidth: 76 }}>
                              {period ? (
                                <Link to={`/shift/periods/${period.period_id}/board`} className="hover:underline text-misespark-navy">
                                  <DateLabel date={date} withWeekday holidays={holidays} />
                                </Link>
                              ) : <DateLabel date={date} withWeekday holidays={holidays} />}
                              {cell && (cell.violation_counts.hard > 0 || cell.violation_counts.soft > 0) && (
                                <span className="sr-only"> (違反あり)</span>
                              )}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {displayRows.map((row) => (
                        <tr key={row.key} className="odd:bg-white even:bg-surface-stripe">
                          <th
                            scope="row" title={row.labelText}
                            className="sticky left-0 z-10 bg-inherit border-r border-surface-border px-3 py-1.5 text-left text-xs text-text-sub whitespace-nowrap max-w-[160px] truncate"
                          >
                            {row.label}
                          </th>
                          {dates.map((date) => {
                            const compositeKey = `${node.node_id}|${date}`;
                            const cell = cellsByNodeDate.get(compositeKey);
                            return (
                              <td key={date} className="px-2 py-1.5 text-center text-xs whitespace-nowrap">
                                {row.render(compositeKey, cell)}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>

          {/* <768px: 日付ドリル + メトリクス縦カード (U-10) */}
          <div className="md:hidden space-y-3">
            <div className="flex items-center justify-between border border-surface-border rounded-lg bg-white p-2">
              <button
                type="button" aria-label="前日"
                disabled={!mobileDate && dates.length === 0}
                onClick={() => setMobileDate((d) => { const cur = d ?? dates[0] ?? from; const idx = dates.indexOf(cur); return dates[Math.max(0, idx - 1)] ?? cur; })}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded hover:bg-surface-sunken"
              >
                <ChevronLeft size={18} aria-hidden />
              </button>
              <span className="text-sm font-medium">
                <DateLabel date={mobileDate ?? dates[0] ?? from} withWeekday withYear holidays={holidays} />
              </span>
              <button
                type="button" aria-label="翌日"
                onClick={() => setMobileDate((d) => { const cur = d ?? dates[0] ?? from; const idx = dates.indexOf(cur); return dates[Math.min(dates.length - 1, idx + 1)] ?? cur; })}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded hover:bg-surface-sunken"
              >
                <ChevronRight size={18} aria-hidden />
              </button>
            </div>
            {data.nodes.map((node) => {
              const date = mobileDate ?? dates[0] ?? from;
              const cell = cellsByNodeDate.get(`${node.node_id}|${date}`);
              const period = findPeriodFor(node.node_id, date);
              return (
                <div key={node.node_id} className="border border-surface-border rounded-lg bg-white p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-sm">{node.node_name}</span>
                    {period && <Link to={`/shift/periods/${period.period_id}/board`} className="text-xs text-misespark-navy hover:underline min-h-[44px] flex items-center">編成ボードへ</Link>}
                  </div>
                  {cell ? (
                    <dl className="grid grid-cols-2 gap-2 text-xs">
                      {displayRows.map((row) => (
                        <div key={row.key} className="flex flex-col">
                          <dt className="text-text-sub">{row.label}</dt>
                          <dd>{row.render(`${node.node_id}|${date}`, cell)}</dd>
                        </div>
                      ))}
                    </dl>
                  ) : <span className="text-xs text-text-sub">データがありません</span>}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
